import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { clockService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { TicketView } from '@unpispas/upp-data';
import { ViewRenderer } from '@unpispas/upp-wdgt';

type ThumbPriceParts = { int: string; dec: string };

/**
 * Guest list row for a ticket: paid receipt, ready-order, or pending request.
 * Emits `Open` so the host can show the full receipt / preview.
 */
@Component({
    selector: 'upp-guest-ticket-thumb',
    templateUrl: './upp-thumb.html',
    styleUrls: ['./upp-thumb.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestTicketThumbComponent extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) ticket!: TicketView;
    /** `PAID` (default) | `READY` | `PENDING`. */
    @Input() mode: 'PAID' | 'READY' | 'PENDING' = 'PAID';
    /** Pending AskWaiter reason string (`REFUND` / `PAYCASH` / `PAYCARD`); other/null → PP copy. */
    @Input() reason: string | null = null;
    /** Absolute amount override (e.g. AskWaiter.amount for REFUND). */
    @Input() amount: number | null = null;
    @Output() Open = new EventEmitter<TicketView>();

    private _ticket_subscription: Subscription | null = null;
    private _minute_subscription: Subscription | null = null;
    private _elapsed: string | null = null;

    constructor(
        lang: languageService,
        private change: ViewRenderer,
        private clock: clockService,
    ) {
        super(lang, change.cdref);
    }

    get IsPaid(): boolean {
        return this.mode === 'PAID';
    }

    get IsReady(): boolean {
        return this.mode === 'READY';
    }

    get IsPending(): boolean {
        return this.mode === 'PENDING';
    }

    get HasInvoice(): boolean {
        return !!this.ticket?.invoice;
    }

    get TitleKey(): string {
        return this.HasInvoice
            ? '@place_invoice_payment_done'
            : '@place_ticket_payment_done';
    }

    get IconName(): string {
        return this.HasInvoice
            ? 'document-text-outline'
            : 'receipt-outline';
    }

    get StatusKey(): string | null {
        if (!this.IsPending) {
            return null;
        }
        if (this.reason === 'REFUND') {
            return '@place_nowaiter_refund_pending';
        }
        if (this.reason === 'PAYCASH') {
            return '@place_nowaiter_paycash_pending';
        }
        if (this.reason === 'PAYCARD') {
            return '@place_nowaiter_paycard_pending';
        }
        return '@place_event_type_PP';
    }

    get ActionKey(): string {
        if (this.IsReady) {
            return '@place_ready_ticket_message';
        }
        return '@place_nowaiter_ticket_action';
    }

    get ShowPrice(): boolean {
        return this.IsPending;
    }

    get IsNegative(): boolean {
        if (this.reason === 'REFUND') {
            return true;
        }
        return this.amount !== null && this.amount < 0;
    }

    get PriceParts(): ThumbPriceParts | null {
        if (!this.ShowPrice) {
            return null;
        }
        if (this.amount !== null) {
            return UppGuestTicketThumbComponent._FormatAmount(this.amount);
        }
        const _info = this.ticket?.priceinfo;
        if (!_info) {
            return null;
        }
        return { int: _info.int, dec: _info.dec };
    }

    get Elapsed(): string {
        if (this._elapsed === null) {
            this._elapsed = this._BuildElapsed();
        }
        return this._elapsed;
    }

    ngOnInit(): void {
        this._minute_subscription = this.clock.OnOneMinuteTick.subscribe(() => {
            this._elapsed = null;
            this.change.markForCheck();
        });
    }

    ngOnChanges(): void {
        this._BindTicket();
        this._elapsed = null;
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        if (this._ticket_subscription) {
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        if (this._minute_subscription) {
            this._minute_subscription.unsubscribe();
            this._minute_subscription = null;
        }

        super.OnDestroy();
    }

    OnOpen(): void {
        if (!this.ticket) {
            return;
        }
        this.Open.emit(this.ticket);
    }

    private _BindTicket(): void {
        if (this._ticket_subscription) {
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        if (this.ticket) {
            this._ticket_subscription = this.ticket.OnViewRefresh.subscribe(() => {
                this._elapsed = null;
                this.change.markForCheck();
            });
        }
    }

    private _BuildElapsed(): string {
        const _ref = this.ticket?.updated || this.ticket?.created;
        if (!_ref) {
            return this.tr('@paid_time_just_now');
        }

        const _min = Math.round((Date.now() - _ref.getTime()) / 60000);

        if (_min < 1) {
            return this.tr('@paid_time_just_now');
        }

        if (_min < 60) {
            return this.tr('@paid_time_minutes', [ _min ]);
        }

        const _hh = Math.round(_min / 60);
        const _mm = Math.round(_min % 60);
        return this.tr('@paid_time_hours', [ _hh, _mm ]);
    }

    private static _FormatAmount(amount: number): ThumbPriceParts {
        const _parts = Math.abs(amount).toString().split('.');
        const _int = _parts[0];
        const _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : '00';
        return { int: _int, dec: _dec };
    }
}
